import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import GetAppIcon from '@material-ui/icons/GetApp';
import VisibilityIcon from '@material-ui/icons/Visibility';

import DeleteDialog from '../../../../components/DeleteDialog';
import Loader from '../../../../components/Loader';
import { useQuery } from '../../../../constants';
import FilterBar from '../../components/FilterBar';
import PageHeader from '../../components/PageHeader';
import AiImageDownloadDialog from '../../components/AiImageDownloadDialog';
import EnhancedTable from '../../components/Table';
import ImageAssetView from '../../components/ImageAssetView';
import {
  deleteAiLibraryAsset,
  fetchAiLibraryAssets,
  getLibraryAssetDownloadUrl,
  upscaleAiLibraryAsset,
  checkUpscaleStatus,
} from '../../redux/actions/ai';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
}));

const headCells = [
  {
    id: 'image',
    numeric: false,
    disablePadding: false,
    label: 'Image',
    width: '80px',
    disableSorting: true,
    formatter: (row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {row.thumbnailUrl && (
          <img
            src={row.thumbnailUrl || row.imageUrl}
            alt="Saved asset"
            className="zoomable-thumbnail"
            style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee', cursor: row.imageUrl ? 'pointer' : 'default' }}
            onClick={() => { if (row.imageUrl) document.dispatchEvent(new CustomEvent('view-library-asset', { detail: row })); }}
            onError={(e) => {
              if (row.imageUrl && e.target.src !== row.imageUrl) {
                e.target.src = row.imageUrl;
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        )}
      </div>
    )
  },
  {
    id: 'title',
    numeric: false,
    disablePadding: false,
    label: 'Title',
    formatter: (row) => row.title || 'Untitled'
  },
  {
    id: 'createdAt',
    numeric: false,
    disablePadding: false,
    label: 'Date Created',
    formatter: (row) => dayjs(row.createdAt).format('MMM D, YYYY')
  },
  {
    id: 'actions',
    numeric: true,
    disablePadding: false,
    label: 'Actions',
    disableSorting: true,
    actions: [
      {
        label: 'View',
        action: (row) => { if (row.imageUrl) document.dispatchEvent(new CustomEvent('view-library-asset', { detail: row })); },
        icon: <VisibilityIcon fontSize="small" />,
        disabled: (row) => !row.imageUrl,
      },
      {
        label: 'Download',
        action: (row) => document.dispatchEvent(new CustomEvent('download-library-asset', { detail: row })),
        icon: <GetAppIcon fontSize="small" />,
        disabled: (row) => !row.imageUrl,
      },
      {
        label: 'Unsave',
        action: (row) => document.dispatchEvent(new CustomEvent('unsave-library-asset', { detail: row })),
        icon: <DeleteForeverIcon fontSize="small" color="secondary" />,
        disabled: () => false,
      }
    ]
  },
];

export default function AiLibrary() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const page = useQuery('page') || 1;
  const limit = useQuery('limit') || 25;

  const { fetching, actionLoading, libraryAssets } = useSelector(({ admin }) => admin.ai);

  const [sort, setSort] = useState({ orderBy: 'createdAt', orderDir: 'desc' });

  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewImageUrl, setViewImageUrl] = useState('');
  const [downloadAsset, setDownloadAsset] = useState(null);

  useEffect(() => {
    dispatch(fetchAiLibraryAssets(page, limit, search, sort.orderBy, sort.orderDir));
  }, [dispatch, page, limit, search, sort]);

  const handleSearch = (val) => {
    setSearch(val);
  };

  const handleDelete = async () => {
    if (selectedAsset) {
      const success = await dispatch(deleteAiLibraryAsset(selectedAsset._id));
      if (success) {
        setDeleteDialogOpen(false);
        setSelectedAsset(null);
        dispatch(fetchAiLibraryAssets(page, limit, search));
      }
    }
  };

  const openDeleteDialog = (asset) => {
    setSelectedAsset(asset);
    setDeleteDialogOpen(true);
  };

  useEffect(() => {
    const handleDeleteEvent = (e) => openDeleteDialog(e.detail);
    const handleViewEvent = (e) => {
      setViewImageUrl(e.detail.imageUrl);
      setViewDialogOpen(true);
    };
    const handleDownloadEvent = (e) => setDownloadAsset(e.detail);

    document.addEventListener('unsave-library-asset', handleDeleteEvent);
    document.addEventListener('view-library-asset', handleViewEvent);
    document.addEventListener('download-library-asset', handleDownloadEvent);

    return () => {
      document.removeEventListener('unsave-library-asset', handleDeleteEvent);
      document.removeEventListener('view-library-asset', handleViewEvent);
      document.removeEventListener('download-library-asset', handleDownloadEvent);
    };
  }, []);

  return (
    <div className={classes.root}>
      {fetching && <Loader fetching={fetching} />}

      <PageHeader
        title="AI Assets"
        disableSearch
      />

      <FilterBar
        searchValue={search}
        onSearch={handleSearch}
        enableTopics={false}
      />

      {libraryAssets && libraryAssets.docs && libraryAssets.docs.length > 0 ? (
        <ImageAssetView
          data={libraryAssets}
          handlePage={(p) => dispatch(fetchAiLibraryAssets(p, limit, search, sort.orderBy, sort.orderDir))}
          getImage={(row) => ({
            src: row.thumbnailUrl || row.imageUrl,
            fallback: row.imageUrl,
            alt: row.title || 'Saved asset',
          })}
          getTitle={(row) => row.title || 'Untitled'}
          getSubtitle={(row) => (row.createdAt ? dayjs(row.createdAt).format('MMM D, YYYY') : '')}
          onImageClick={(row) => {
            if (!row.imageUrl) return;
            setViewImageUrl(row.imageUrl);
            setViewDialogOpen(true);
          }}
          actions={[
            {
              label: 'View',
              icon: <VisibilityIcon fontSize="small" />,
              onClick: (row) => {
                if (!row.imageUrl) return;
                setViewImageUrl(row.imageUrl);
                setViewDialogOpen(true);
              },
              disabled: (row) => !row.imageUrl,
            },
            {
              label: 'Download',
              icon: <GetAppIcon fontSize="small" />,
              onClick: (row) => setDownloadAsset(row),
              disabled: (row) => !row.imageUrl,
            },
            {
              label: 'Unsave',
              icon: <DeleteForeverIcon fontSize="small" color="secondary" />,
              onClick: openDeleteDialog,
            },
          ]}
          table={(
            <EnhancedTable
              data={libraryAssets}
              headCells={headCells}
              handlePage={(p) => dispatch(fetchAiLibraryAssets(p, limit, search, sort.orderBy, sort.orderDir))}
              orderField={sort.orderBy}
              orderDirection={sort.orderDir}
              handleOrder={(s) => setSort(s)}
            />
          )}
        />
      ) : (
        !fetching && (
          <Typography color="textSecondary" style={{ marginTop: 16 }}>
            No saved images in library.
          </Typography>
        )
      )}

      {/* Delete Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        handleConfirm={handleDelete}
        loading={actionLoading}
        title="Unsave Image"
        content="Are you sure you want to remove this image from your AI Assets? It will still remain in the original chat session."
      />

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent style={{ position: 'relative', padding: 0, overflow: 'hidden' }}>
          <IconButton
            style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', color: 'white' }}
            onClick={() => setViewDialogOpen(false)}
          >
            <CloseIcon />
          </IconButton>
          <img src={viewImageUrl} alt="Preview" style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block' }} />
        </DialogContent>
      </Dialog>

      <AiImageDownloadDialog
        open={Boolean(downloadAsset)}
        imageUrl={downloadAsset?.imageUrl || ''}
        onClose={() => setDownloadAsset(null)}
        onUpscale={downloadAsset ? () => ({
          initiate: () => upscaleAiLibraryAsset(downloadAsset._id),
          checkStatus: () => checkUpscaleStatus(downloadAsset.runId, downloadAsset.messageIndex)
        }) : null}
        onDownloadOriginal={downloadAsset ? () => getLibraryAssetDownloadUrl(downloadAsset._id) : null}
      />
    </div>
  );
}
